/**
 * Block-level markdown for text the analyst wrote.
 *
 * `AskTab` deferred this on purpose — `whitespace-pre-wrap` keeps prose legible
 * and a library was not worth adding for paragraphs. What broke the deferral is
 * the analyst's tables: a GFM pipe table read as plain text is a column of
 * `|---|---|` and numbers whose header a reader has to count out by hand, and
 * ranked tiers, coverage fractions and `null`-versus-`0` cells are most of what
 * this surface answers with. That is the "unreadable without it" the deferral
 * named, so the library is here now.
 *
 * `react-markdown`, not a hand-rolled parser and not `marked` + `innerHTML`. It
 * renders to React elements and drops raw HTML unless `rehype-raw` is added, so
 * nothing the model writes — quoting KG labels, literature titles and catalog ids
 * it did not author — can reach the DOM as markup. `remark-gfm` is what makes the
 * tables, strikethrough and task lists parse at all; without it they fall back to
 * the same plain text this replaces.
 *
 * The `components` map exists because this project has no
 * `@tailwindcss/typography`. Adding the plugin to style one component would style
 * every `.prose` in an app that has none, and its defaults are tuned for light
 * documents rather than this palette. These are the same slate classes the Wiki
 * tables and the Ask tab's tool trail already use.
 *
 * Renders on every streamed token, deliberately. Parsing a few KB per token is
 * cheap next to the model producing it, and the alternative — plain text while
 * streaming, markdown once done — reflows the whole answer at the end, which
 * reads as the answer being rewritten.
 */
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Components } from 'react-markdown'
import { cn } from '../../lib/utils'

const COMPONENTS: Components = {
  p: ({ children }) => <p className="mb-2 last:mb-0 break-words">{children}</p>,
  h1: ({ children }) => (
    <h3 className="mb-1 mt-3 font-display text-sm font-semibold text-slate-100 first:mt-0">
      {children}
    </h3>
  ),
  h2: ({ children }) => (
    <h4 className="mb-1 mt-3 font-display text-sm font-semibold text-slate-100 first:mt-0">
      {children}
    </h4>
  ),
  h3: ({ children }) => (
    <h5 className="mb-1 mt-3 font-display text-xs font-semibold uppercase tracking-eyebrow text-slate-400 first:mt-0">
      {children}
    </h5>
  ),
  ul: ({ children }) => (
    <ul className="mb-2 list-disc space-y-0.5 pl-5 last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-decimal space-y-0.5 pl-5 last:mb-0">{children}</ol>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="text-brand-300 underline underline-offset-2 hover:text-brand-200"
    >
      {children}
    </a>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-100">{children}</strong>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-2 border-slate-700 pl-3 text-slate-400 last:mb-0">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-3 border-slate-800" />,
  // `pre` carries the scroll and the frame; the `code` inside it must not also
  // get the inline pill, so it is detected by the wrapper rather than by trying
  // to tell inline from block inside `code` itself.
  pre: ({ children }) => (
    <pre className="mb-2 overflow-x-auto rounded border border-slate-800 bg-slate-950/70 p-2 text-2xs leading-relaxed last:mb-0 [&_code]:bg-transparent [&_code]:p-0">
      {children}
    </pre>
  ),
  code: ({ children }) => (
    <code className="rounded bg-slate-800/80 px-1 py-0.5 font-mono text-[0.95em] text-slate-300">
      {children}
    </code>
  ),
  // Wrapped rather than sized: a table of eleven ranked targets is wider than
  // this column, and the choice is a horizontal scrollbar or cells that wrap a
  // tier letter onto its own line.
  table: ({ children }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="w-full text-left text-2xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="text-slate-500">{children}</thead>,
  tbody: ({ children }) => <tbody className="align-top">{children}</tbody>,
  tr: ({ children }) => (
    <tr className="border-t border-slate-800/70 first:border-t-0">{children}</tr>
  ),
  th: ({ children, style }) => (
    <th
      scope="col"
      style={style}
      className="whitespace-nowrap py-1 pr-3 font-medium last:pr-0"
    >
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td style={style} className="py-1.5 pr-3 text-slate-300 last:pr-0">
      {children}
    </td>
  ),
}

export function Markdown({
  text,
  className,
}: {
  text: string
  className?: string
}) {
  return (
    <div className={cn('break-words', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
        {text}
      </ReactMarkdown>
    </div>
  )
}
